import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Grid,
  IconButton,
  Typography,
  Alert,
} from '@mui/material';
import { Close, Save, PersonAdd } from '@mui/icons-material';
import * as Actions from 'actions';

const ContactModal = (props) => {
  const { open, onClose, contact, isLoading, serviceError, reqStatus, updateContact, createContact } = props;
  
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    address: '',
    notes: '',
  });
  const [errors, setErrors] = useState({});

  const isEditMode = !!contact;

  // Pre-fill form when editing
  useEffect(() => {
    if (contact) {
      setFormData({
        name: contact.name || '',
        mobile: contact.mobile || '',
        address: contact.address || '',
        notes: contact.notes || '',
      });
    }
  }, [contact]);

  // Close modal on success
  useEffect(() => {
    if (
      reqStatus === 'SELLER_CREATE_CONTACT_SUCCESS' ||
      reqStatus === 'SELLER_UPDATE_CONTACT_SUCCESS'
    ) {
      handleClose();
    }
  }, [reqStatus]);

  // Reset form when modal opens
  useEffect(() => {
    if (open && !contact) {
      resetForm();
    }
  }, [open, contact]);

  const resetForm = () => {
    setFormData({
      name: '',
      mobile: '',
      address: '',
      notes: '',
    });
    setErrors({});
  };

  const validate = () => {
    const newErrors = {};
    const { name, mobile } = formData;

    if (!name || name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    if (!mobile || mobile.trim().length < 7) {
      newErrors.mobile = 'Mobile number must be at least 7 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) {
      return;
    }

    const { name, mobile, address, notes } = formData;
    const contactData = {
      name: name.trim(),
      mobile: mobile.trim(),
      address: address.trim() || undefined,
      notes: notes.trim() || undefined,
    };

    if (contact) {
      // Update existing contact
      updateContact(contact._id, contactData);
    } else {
      // Create new contact
      createContact(contactData);
    }
  };

  const handleClose = () => {
    resetForm();
    if (onClose) {
      onClose();
    }
  };

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
    setErrors({
      ...errors,
      [field]: null,
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PersonAdd sx={{ mr: 1 }} />
            <Typography variant="h6">
              {isEditMode ? 'Edit Contact' : 'Add New Contact'}
            </Typography>
          </Box>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {serviceError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {serviceError}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Name"
              fullWidth
              required
              value={formData.name}
              onChange={handleChange('name')}
              error={!!errors.name}
              helperText={errors.name}
              disabled={isLoading}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Mobile Number"
              fullWidth
              required
              value={formData.mobile}
              onChange={handleChange('mobile')}
              error={!!errors.mobile}
              helperText={errors.mobile || 'Enter numbers only'}
              disabled={isLoading || isEditMode}
              InputProps={{
                readOnly: isEditMode,
              }}
            />
            {isEditMode && (
              <Typography variant="caption" color="text.secondary">
                Mobile number cannot be changed
              </Typography>
            )}
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Address"
              fullWidth
              multiline
              rows={2}
              value={formData.address}
              onChange={handleChange('address')}
              disabled={isLoading}
              placeholder="Enter full address"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Notes"
              fullWidth
              multiline
              rows={3}
              value={formData.notes}
              onChange={handleChange('notes')}
              disabled={isLoading}
              placeholder="Additional notes or comments"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          startIcon={<Save />}
          disabled={isLoading}
        >
          {isLoading ? 'Saving...' : isEditMode ? 'Update Contact' : 'Save Contact'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const _ContactModal = connect(
  ({ sellerContacts }) => ({
    isLoading: sellerContacts.isLoading,
    serviceError: sellerContacts.serviceError,
    reqStatus: sellerContacts.reqStatus,
  }),
  (dispatch) => bindActionCreators({ ...Actions }, dispatch)
)(ContactModal);

export { _ContactModal as default };
